import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Table,
  Text,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdDelete, MdLock } from "react-icons/md";
import { authService } from "services/authService";
import {
  CustomerToolkitAuthorization,
  customerService,
} from "services/customerService";

const TYPE_LABEL_KEYS: Record<string, string> = {
  BasicAuth: "app.customers.details.basicAuth",
  CustomToken: "app.customers.details.customToken",
  OAuth: "app.customers.details.oauth",
  CustomAuthEndpoint: "app.customers.details.customAuthEndpoint",
};

interface CustomerAuthorizationCardProps {
  customerId: string;
  formatDate: (dateString: string) => string;
}

const CustomerAuthorizationCard = ({
  customerId,
  formatDate,
}: CustomerAuthorizationCardProps) => {
  const { t } = useTranslation();
  const isAdmin = authService.isAdmin();
  const [authorizations, setAuthorizations] = useState<
    CustomerToolkitAuthorization[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadAuthorizations = async () => {
    try {
      setLoading(true);
      setAuthorizations(
        await customerService.getCustomerAuthorizations(customerId),
      );
      setError(null);
    } catch (err: any) {
      setError(
        err.response?.data?.error || t("app.customers.details.authLoadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuthorizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleClear = async () => {
    try {
      setClearing(true);
      await customerService.clearCustomerAuthorization(customerId);
      setShowClearModal(false);
      await loadAuthorizations();
    } catch (err: any) {
      setShowClearModal(false);
      setError(
        err.response?.data?.error || t("app.customers.details.clearAuthFailed"),
      );
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <Card.Root>
        <Card.Body p={6}>
          <Flex justify="space-between" align="center" mb={2}>
            <HStack>
              <MdLock />
              <Heading size="md">
                {t("app.customers.details.authorization")}
              </Heading>
            </HStack>
            {isAdmin && authorizations.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                colorPalette="red"
                onClick={() => setShowClearModal(true)}
              >
                <MdDelete /> {t("app.customers.details.clearAuthorization")}
              </Button>
            )}
          </Flex>
          <Text fontSize="sm" color="var(--text-secondary-color)" mb={4}>
            {t("app.customers.details.authorizationDescription")}
          </Text>

          {error && (
            <Text
              fontSize="sm"
              color={{ base: "red.600", _dark: "red.400" }}
              mb={3}
            >
              {error}
            </Text>
          )}

          {loading ? (
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.common.loading")}
            </Text>
          ) : authorizations.length === 0 ? (
            !error && (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customers.details.noAuthorization")}
              </Text>
            )
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm" variant="outline" minW="420px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.customers.details.toolkit")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customers.details.authType")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customers.details.savedAt")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customers.details.lastUpdated")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {authorizations.map((auth) => (
                    <Table.Row key={auth.serverId}>
                      <Table.Cell fontWeight="medium">
                        {auth.serverName ??
                          t("app.customers.details.deletedToolkit")}
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant="subtle">
                          {TYPE_LABEL_KEYS[auth.authorizationType]
                            ? t(TYPE_LABEL_KEYS[auth.authorizationType])
                            : auth.authorizationType}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell fontSize="xs">
                        {formatDate(auth.createdAt)}
                      </Table.Cell>
                      <Table.Cell fontSize="xs">
                        {formatDate(auth.updatedAt)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Card.Body>
      </Card.Root>

      {showClearModal && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.5)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex="1000002"
        >
          <Box
            bg={{ base: "white", _dark: "navy.800" }}
            p={6}
            borderRadius="lg"
            maxW="450px"
            w="90%"
            boxShadow="2xl"
          >
            <Heading
              size="md"
              mb={3}
              color={{ base: "red.600", _dark: "red.400" }}
            >
              {t("app.customers.details.clearAuthorization")}
            </Heading>
            <Text
              mb={4}
              fontSize="sm"
              color={{ base: "gray.700", _dark: "gray.300" }}
            >
              {t("app.customers.details.clearAuthConfirm")}
            </Text>
            <HStack justifyContent="flex-end" gap={3}>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={() => setShowClearModal(false)}
                disabled={clearing}
              >
                {t("app.customers.details.cancel")}
              </Button>
              <Button
                colorScheme="red"
                onClick={handleClear}
                loading={clearing}
              >
                {clearing
                  ? t("app.customers.details.clearingAuth")
                  : t("app.customers.details.clearAuthorization")}
              </Button>
            </HStack>
          </Box>
        </Box>
      )}
    </>
  );
};

export default CustomerAuthorizationCard;
